/**
 * The copyright service implementation provides a full copyrights notice in an HTML format that can be displayed to
 * the user when the copyrights link is clicked. A text is wrapped in a 'div' element with id 'copyrightMessage'.
 * Country names are enclosed in the 'h4' elements and the copyrights information per each supplier is in 'p' elements.
 * This makes styling with CSS fairly easy.
 *
 * The copyrights API can take as argument area which the copyrights concerns. If this argument is omitted the whole
 * world is considered.
 *
 * The API handles the "edge of the world" issue. Allowing to properly handle bounding box that is outside of world
 * extends (-180, -90, 180, 90).
 *
 * The tomtom.copyright() method creates a new instance of the service. Parameter can be passed on its creation or
 * provided via a convenient function that can be chained. The method go() performs the actual call.
 *
 * The call is asynchronous, therefore the user have two options to handle response, either by passing a callback
 * function or by using
 * [Promise](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise). The SDK
 * provides a polyfill for browsers (IE<9) without native Ecma Script 6 promise support.
 *
 * @class copyright
 */
define('services/copyright',["rest/copyright", "model/copyright/copyright", "./chain", "./validators", "./converters", "./worldEdge"],
    function (copyright, model, chain, validators, converters, worldEdge) {
        "use strict";

        var fields = {
            /**
             * A setter and getter for the _'key'_ attribute. Key can be set globally when initializing a map, with use
             * of either the tomtom.key or tomtom.mapsKey function. But also per service instance on
             * initialization or with this method.
             *
             * An API Key can be issued from http://developer.tomtom.com
             *
             * @method key
             * @chainable
             * @param {String} key API key valid for requested service.
             * @throws {TypeError} If the given argument cannot be converted to a valid value
             * @return {copyright|String} Returns the service instance that allows chaining or a key value if
             *     argument is omitted.
             */
            key: {validator: validators.string},
            /**
             * A setter and getter for the _'boundingBox'_ attribute. The bounding box describes an area that
             * copyrights information is needed for. It can be omitted, then the whole world is taken into
             * consideration.
             *
             * Bounding box can exceed world boundaries (-180, -90, 180, 90) it is not an issue. The service will
             * internally normalize it by overwriting any exceeding value with maximum or minimum allowed. Longitude
             * is recalculated to fit the allowed range. If the bounding box includes "the edge of the world", it is
             * split into two bounding boxes and the response will contain merged results for both areas.
             *
             * The service is able to convert a number of popular formats into the bounding box. Supported formats are
             * listed below:
             * * _{minLon: 0, minLat: 0, maxLon: 1, maxLat: 1}_ // plain object with minLon, minLat, maxLon, maxLat
             * fields
             * * _L.latLngBounds(...)_ // leaflet bounds object
             * * _[0, 0, 1, 1]_ // array of numbers containing in order minLon, minLat, maxLon, maxLat
             * * _ol.Extent_ // open layers extend object
             * * _[ol.Coordinate, ol.Coordinate]_ // array of two open layers coordinate objects in order southWest,
             * northEast
             * * _[[0, 0], [1, 1]]_ // array of two arrays (southWest, northEast) with latitude and longitude values
             * * _"0,0,1,1"_ // comma seperated string of number values minLon, minLat, maxLon, maxLat
             * * _[{lon: 0, lat: 0},{lon: 1, lat: 1}]_ // array of two plain objects (southWest, northEast) with lat
             * and lon fields
             * * _[{lng: 0, lat: 0},{lng: 1, lat: 1}]_ // array of two plain objects (southWest, northEast) with lat
             * and lng fields
             * * _[L.latLng(...), L.latLng(...)]_ // array of southWest and northEast leaflet latLng objects
             * * _google.maps.LatLngBounds_ // Google Maps bounds object
             * * _google.maps.LatLngBoundsLiteral_ // Google Maps bounds object
             * * _[LatLng, LatLng]_ // array of two google.maps.LatLng
             * * _[LatLngLiteral, LatLngLiteral]_ // array of two google.maps.LatLngLiteral
             *
             * @method boundingBox
             * @chainable
             * @param {Any} [boundingBox] The bounding box of the area that copyright are supposed to be retrieved.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {copyright|String} Returns the service instance that allows chaining or a converted bounding
             *     box value if the argument is omitted.
             */
            boundingBox: {validator: validators.boundingBox, converter: converters.boundingBox},
            /**
             * A setter and getter for the _'protocol'_ attribute. Values represents a protocol to be used for
             * performing a service call.
             *
             * @method protocol
             * @chainable
             * @param {String} [protocol] A protocol to be used - either _http_ or _https_.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {copyright|String} Returns the service instance that allows chaining or a _protocol_
             * value, if the argument is omitted.
             */
            protocol: {validator: validators.oneOfValue(["http", "https"], "protocol")},
            baseUrl: {validator: validators.string},
            serviceUrl: {validator: validators.string}
        };

        /**
         * The function performing an actual call to the web service.
         *
         * The callback function takes a response parameter and the fail callback takes error as the only parameter.
         *
         * The final response from the service is an HTML of the copyrights information to be displayed. A text is
         * wrapped in a 'div' element with id 'copyrightMessage'. Country names are enclosed in the 'h4' elements and
         * the copyrights information per each supplier is in 'p' elements. This makes styling with CSS fairly easy. An
         * example response may look like this:
         *```html
         *
         * <div id="copyrightMessage"><h4>General Copyrights:</h4>
         *   <p>© 1992 – 2016 TomTom. All rights reserved. This
         *   material is proprietary and the subject of
         *   copyright protection, database right protection
         *   and other intellectual property rights owned
         *   by TomTom or its suppliers. The use of
         *   this material is subject to the terms of a license
         *   agreement. Any unauthorized copying or disclosure
         *   of this material will lead to criminal and civil
         *   liabilities.</p>
         *   <p>Data Source © 2016 TomTom</p>
         * </div>
         * ```
         *
         * @method go
         * @param {Function} [callback] The callback function to be called on success.
         * @param {Function} [fail] The callback function to be called on failure. If success callback is not passed
         *     fail callback is ignored. Returned promise can handle exceptional behaviour i.e. with then() method.
         * @return {Promise} If callback is omitted function returns promise.
         *
         * @example
         * ```javascript
         *
         * var myFunction = function(result) { console.log(result); };
         * // this is optional if you passed global key earlier
         * tomtom.copyrightKey(<Your API key>);
         * var copyright = tomtom.copyright()
         *   .boundingBox("-180,-90,180,90").callback(myFunction).go();
         * ```
         */
        /**
         * A setter and getter for the callback function. This function is called after receiving successful response
         * from the service. It takes a single argument which is an HTML response from the service.
         *
         * @method callback
         * @chainable
         * @param {Function} [callback] The callback function to be called on success.
         * @throws {TypeError} If the given argument is not a function.
         * @return {copyright|Function} Returns the service instance that allows chaining or a function, if the argument
         *     is omitted.
         */
        /**
         * A setter and getter for the fail callback function. This function is called after an unsuccessful response
         * from the service or after a communication error. It takes one argument which is the error description.
         *
         * This parameter is ignored if callback function is not passed and therefore promise mechanism is used to
         * handle the response. When using promises, errors are handled with the second attribute of the then() method
         * or with the catch() method.
         *
         * By default the fail function is:
         * ```javascript
         *
         * function(error) {
         *   console.error(error);
         *   throw new Error(error);
         * }
         * ```
         * @method fail
         * @chainable
         * @param {Function} [fail] The callback function to be called on a failure. By default, an error is logged on
         *     the console and thrown.
         * @throws {TypeError} If the given argument is not a function.
         * @return {copyright|Function} Returns the service instance that allows chaining or a function, if the argument
         *     is omitted.
         */
        function addBoundingBoxParameters(data, boundingBox) {
            data.minLon = boundingBox.minLon;
            data.maxLon = boundingBox.maxLon;
            data.minLat = boundingBox.minLat;
            data.maxLat = boundingBox.maxLat;
            return data;
        }

        function handleChain(data, callback) {
            worldEdge(copyright.copyrights, model, data, callback, addBoundingBoxParameters);
        }

        /**
         * The method that instantiates the service. The options parameter can be passed on the creation or later
         * during the execution - by the function with a name matching a desired parameter's name. Each option can be
         * retrieved using exactly the same function but without a parameter. An instance of the service can be reused
         * by calling the go() function multiple times.
         *
         * @method copyright
         * @param {Object} [options] Options to be passed to the copyrightCaption call.
         * @param {String} [options.key] This is optional if a key was already passed.
         * @param {Object|Array|String} [options.boundingBox] An area to extract the copyrights information for. If
         *     omitted, the whole world will be taken into consideration. Conversion objects are described with the
         *     boudingBox() function.
         * @param {Boolean} [options.protocol] A protocol to be used - either "http" or "https".
         * @return {copyright} Returns a new object that allows communication with the service.
         *
         * @example
         * ```javascript
         *
         * tomtom.copyright().go().then(function(response) {
         *   console.log(response);
         * });
         * ```
         */
        return function (options) {
            return chain(fields, handleChain, options);
        };
    });

